import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { KBOPlayerYearStatsListItemDto } from '@playball/common/dto/KBO';
import AdComponent from './ads/AdComponent';
import { DataListResponse } from '@playball/common/dto/Common';

const POSITION_MAP: { [key: string]: string } = {
  'P': '투수', '1B': '1루수', '2B': '2루수', '3B': '3B', 'SS': '유격수',
  'C': '포수', 'CF': '중견수', 'DH': '지명타자', 'LF': '좌익수', 'RF': '우익수',
}

const RankingPage: React.FC = () => {
  const { fetchApi } = useAuth();
  const [allPlayers, setAllPlayers] = useState<KBOPlayerYearStatsListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const year = 2025;
  const limit = 100;

  const fetchAllPlayers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: DataListResponse<KBOPlayerYearStatsListItemDto> = await fetchApi(`/api/player?year=${year}&limit=${limit}`);
      
      if (Array.isArray(res.dataList)) {
        setAllPlayers(res.dataList as KBOPlayerYearStatsListItemDto[]);
      } else {
        console.error("API /api/player/2025 returned non-array data:", res);
        setError("유효하지 않은 선수 데이터를 수신했습니다.");
      }
    } catch (err: any) {
      console.error("선수 데이터를 불러오는 데 실패했습니다:", err);
      setError("랭킹을 불러오는 데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    fetchAllPlayers();
  }, [fetchAllPlayers]);

  const playerRankings = useMemo(() => {
    return allPlayers
      .sort((a, b) => b.average - a.average)
      .slice(0, 100);
  }, [allPlayers]);

  if (isLoading) {
    return (
      <section className="py-5 bg-light text-center">
        <div className="container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading rankings...</span>
          </div>
          <p className="mt-2 text-muted">랭킹 로딩 중...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 bg-light text-center">
        <div className="container">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
    <section className="guide-hero-section py-5">
      <div className="container text-center">
          <h1 className="display-5 fw-bold">2025 시즌 선수 랭킹 TOP 100</h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
            2025년 시즌 최고의 선수들을 확인하세요.
          </p>
      </div>
    </section>

    <section id="rankings" className="py-1 bg-light keep-all">
      <div className="container py-5">
        <div className="card shadow-sm">
          <div className="card-body">
            <ul className="list-group list-group-flush">
              {playerRankings.length > 0 ? (
                playerRankings.map((player, index) => (
                  <>
                    <li key={player.player_id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                      <span className="fw-semibold me-2" style={{ minWidth: '30px' }}>{index + 1}.</span>
                      <span className="flex-grow-1">
                          <Link to={`/player/${player.player_id}`}>{player.name}</Link> ({player.team}) ({POSITION_MAP[player.position] || player.position})
                      </span>
                    
                      <span className="text-end fw-bold">{player.average ? player.average.toFixed(2) : 0}</span>
                    </li>
                    {
                      index > 0 && index % 20 === 19 && <AdComponent adSlot="list" />
                    }
                  </>
                ))
              ) : (
                <li className="list-group-item text-muted text-center">랭킹 정보가 없습니다.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default RankingPage;
